import { formatCountdown } from "@ccflare/ui";
import { useEffect, useState } from "react";
import { cn } from "../../lib/utils";
import { Progress } from "../ui/progress";

interface CountdownBarProps {
	/** ISO timestamp when the window/turn ends. */
	endIso: string | null;
	/** Total window length in ms; the fill shows elapsed / windowMs. */
	windowMs: number;
	/** Left-hand label, e.g. "Account turn". */
	label: string;
	/** Verb prefixing the countdown, e.g. "switches in" -> "switches in 42m". */
	verb?: string;
	className?: string;
}

/**
 * A labeled progress bar that fills as a fixed-length window elapses toward a
 * known end time. Unlike a usage gauge, the fill is purely temporal — used for
 * ccflare's account-rotation "turn", so it never implies token consumption.
 */
export function CountdownBar({
	endIso,
	windowMs,
	label,
	verb = "ends in",
	className,
}: CountdownBarProps) {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		const interval = setInterval(() => setNow(Date.now()), 10000);
		return () => clearInterval(interval);
	}, []);

	if (!endIso || windowMs <= 0) return null;

	const end = new Date(endIso).getTime();
	const start = end - windowMs;
	const elapsed = now - start;
	const percentage = Math.min(100, Math.max(0, (elapsed / windowMs) * 100));
	const remainingMs = Math.max(0, end - now);

	return (
		<div className={cn("space-y-1.5", className)}>
			<div className="flex items-center justify-between gap-2">
				<span className="text-xs text-muted-foreground">{label}</span>
				<span className="text-xs font-medium text-muted-foreground">
					{remainingMs <= 0 ? "now" : `${verb} ${formatCountdown(remainingMs)}`}
				</span>
			</div>
			<Progress value={percentage} className="h-1.5" />
		</div>
	);
}
